/* istanbul ignore file */

import type { RefObject } from 'react';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import {
  BodyText,
  Button,
  capitalizeFirstLetter,
  IconLink,
  LoadingPlaceholder,
  theme,
  usePressableState,
  useResponsive,
  webOnlyStyles,
} from '@sg/garnish';

import { useLocalizationContext } from '@order/Localization';

import {
  LOCATION_RESULTS_CARD_HEIGHT_PADDING_VERTICAL_SM,
  LOCATION_RESULTS_CARD_HEIGHT_PADDING_VERTICAL_XS,
  LOCATION_RESULTS_CARD_HEIGHT_WITHOUT_PADDING,
  LOCATION_RESULTS_CARD_HEIGHT_WITHOUT_PADDING_SM,
  LOCATION_RESULTS_CARD_HEIGHT_XS,
  LOCATION_RESULTS_CARD_MIN_HEIGHT_SM,
} from '../../LocationResults.constants';
import type {
  Location,
  OnSelectedLocationCardPress,
} from './LocationResultCard.types';
import {
  LocationResultCardDetailsModal,
  LocationResultCardImg,
} from './subcomponents';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationResultsCard = memo((props: LocationResultsCardProps) => {
  const {
    location,
    withBottomBorder,
    locationSearchType,
    withDetailsModal,
    isLoading = false,
    isDisabled = false,
    onPress,
    onLayout,
    onFocus,
    onUmount,
  } = props;
  const {
    id,
    name,
    address,
    city,
    state,
    zipCode,
    imageUrl,
    estimatedDeliveryTime,
    restaurantSlug,
    isClosed,
    acceptingOrders = true,
  } = location;

  const { t } = useLocalizationContext();
  const isVisible = useIsFocused();
  const { match, minWidth } = useResponsive();

  // ─── State ───────────────────────────────────────────────────────────

  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);

  const cardRef = useRef<TouchableOpacity>(null);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const toggleDetailsModal = useCallback(() => {
    setIsDetailsModalVisible((currentValue) => !currentValue);
  }, []);

  const handleOnLayout = useCallback(() => {
    onLayout?.(cardRef, id);
  }, [id, onLayout]);

  const handleOnPress = useCallback(() => {
    const entryPoint =
      locationSearchType === 'delivery' ? 'delivery-cta' : 'card';

    onPress(
      { restaurantId: id, restaurantSlug, restaurantName: name },
      { entryPoint },
    );
  }, [locationSearchType, onPress, id, restaurantSlug, name]);

  const handleOnFocus = useCallback(() => {
    onFocus?.(id);
  }, [id, onFocus]);

  const handleOnUnmount = useCallback(() => {
    onUmount?.(id);
  }, [id, onUmount]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const { isHovered, isFocused } = usePressableState(cardRef);

  const cardStyle = [
    match([styles.cardXS, styles.cardSM]),
    withBottomBorder ? styles.cardWithBorder : undefined,
    isDisabled ? styles.cardDisabled : undefined,
    isHovered || isFocused ? styles.cardHover : undefined,
    isFocused ? webOnlyStyles({ outlineStyle: 'dotted' }) : undefined,
    webOnlyStyles({
      outlineColor: theme.colors.GRAY,
      outlineOffset: theme.spacing['1'],
      outlineWidth: 2,
      transition: `opacity ${theme.transitions.base}ms`,
    }),
  ];

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => handleOnUnmount, [handleOnUnmount]);

  // ─────────────────────────────────────────────────────────────────────

  const shouldDisplayDetailsModal = Boolean(
    withDetailsModal && isDetailsModalVisible && isVisible,
  );
  const shouldDisableControls = isDisabled || isLoading;
  const shouldDisplayClosedNotice =
    locationSearchType !== 'delivery' && !acceptingOrders;
  const shouldDisplayETA = estimatedDeliveryTime !== undefined;
  const shouldDisplayAddressDetails = locationSearchType !== 'delivery';
  const shouldDisplayOrderCTA =
    minWidth.isSM && locationSearchType === 'delivery';

  const ctaLabel = isClosed
    ? t('location.results.card.order-later')
    : t('location.results.card.order-now');

  const hasAddressDetails = Boolean(city && state && zipCode);
  const addressDetails = hasAddressDetails
    ? `${city}, ${state} ${zipCode}`
    : undefined;

  const locationResultA11yName =
    locationSearchType === 'delivery' ? t('general.delivery') : name;

  // ─────────────────────────────────────────────────────────────────────

  const PressableComponent =
    Platform.OS === 'web' ? Pressable : TouchableOpacity;

  return (
    <>
      <PressableComponent
        ref={cardRef}
        style={cardStyle}
        accessibilityRole="button"
        accessibilityLabel={t('location.results.card.a11y', {
          restaurant: locationResultA11yName,
        })}
        disabled={shouldDisableControls}
        onLayout={handleOnLayout}
        onPress={handleOnPress}
        onFocus={onFocus ? handleOnFocus : undefined}
        accessibilityState={{ disabled: isDisabled, busy: isLoading }}
      >
        <View style={match([styles.innerContainerXS, styles.innerContainerSM])}>
          {/* ─── Main Info ─────────────────────────────────────────────────────── */}

          <LocationResultCardImg
            locationName={name}
            locationSearchType={locationSearchType}
            imageUrl={imageUrl}
            isLoading={isLoading}
          />

          <View style={match([styles.infoContainerXS, styles.infoContainerSM])}>
            <BodyText
              bold
              size={match([2, 3])}
              numberOfLines={2}
              style={styles.heading}
              accessibilityLabel={name}
            >
              {capitalizeFirstLetter(name)}
            </BodyText>

            <BodyText
              size={match([3, 4])}
              numberOfLines={1}
              accessibilityLabel={address}
            >
              {address}
            </BodyText>

            {shouldDisplayAddressDetails && addressDetails ? (
              <BodyText
                size={match([3, 4])}
                numberOfLines={1}
                accessibilityLabel={addressDetails}
              >
                {addressDetails}
              </BodyText>
            ) : null}

            {shouldDisplayETA ? (
              <BodyText
                size={match([3, 4])}
                numberOfLines={1}
                accessibilityLabel={estimatedDeliveryTime}
                bold
              >
                {t('delivery.estimate', { estimate: estimatedDeliveryTime })}
              </BodyText>
            ) : null}

            {shouldDisplayClosedNotice ? (
              <BodyText
                size={match([3, 4])}
                numberOfLines={1}
                style={styles.closedLabel}
              >
                {t('location.closed')}
              </BodyText>
            ) : null}
          </View>

          {/* ─── Additional Details ────────────────────────────────────────────── */}

          {withDetailsModal ? (
            <View
              style={match([
                styles.detailsBtnContainerXS,
                styles.detailsBtnContainerSM,
              ])}
            >
              <IconLink
                name="IconBadgeInfo"
                onPress={toggleDetailsModal}
                disabled={shouldDisableControls}
                hitSlop={theme.spacing['1']}
              />
            </View>
          ) : null}
        </View>
      </PressableComponent>

      {shouldDisplayOrderCTA ? (
        <View style={styles.ctaContainer}>
          <Button size="large" isLoading={isLoading} onPress={handleOnPress}>
            {ctaLabel}
          </Button>
        </View>
      ) : null}

      <LocationResultCardDetailsModal
        visible={shouldDisplayDetailsModal}
        location={location}
        locationSearchType={locationSearchType}
        addressDetails={addressDetails}
        isLoading={isLoading}
        isDisabled={isDisabled}
        onRequestClose={toggleDetailsModal}
        onOrderNowBtnPress={onPress}
      />
    </>
  );
});

// ─── Variations ──────────────────────────────────────────────────────────────

export const LocationResultCardLoading = memo(
  (props: Pick<LocationResultsCardProps, 'withBottomBorder'>) => {
    const { withBottomBorder } = props;

    const { match } = useResponsive();

    const cardStyle = [
      match([styles.cardXS, styles.cardSM]),
      withBottomBorder ? styles.cardWithBorder : undefined,
    ];

    return (
      <View style={cardStyle}>
        <LoadingPlaceholder
          rows={1}
          columns={1}
          rowHeight={match([
            LOCATION_RESULTS_CARD_HEIGHT_WITHOUT_PADDING,
            LOCATION_RESULTS_CARD_HEIGHT_WITHOUT_PADDING_SM,
          ])}
          borderRadius={theme.spacing['2']}
          palette="cream"
        />
      </View>
    );
  },
);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  cardXS: {
    height: LOCATION_RESULTS_CARD_HEIGHT_XS,
    paddingVertical: LOCATION_RESULTS_CARD_HEIGHT_PADDING_VERTICAL_XS,
  },
  cardSM: {
    minHeight: LOCATION_RESULTS_CARD_MIN_HEIGHT_SM,
    paddingVertical: LOCATION_RESULTS_CARD_HEIGHT_PADDING_VERTICAL_SM,
  },
  cardWithBorder: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.LIGHT,
  },
  cardHover: {
    opacity: 0.7,
  },
  cardDisabled: {
    opacity: 0.6,
  },
  innerContainerXS: {
    flexDirection: 'row',
  },
  innerContainerSM: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  heading: {
    marginBottom: 2,
  },
  infoContainerXS: {
    flex: 1,
  },
  infoContainerSM: {
    flexBasis: '90%',
  },
  detailsBtnContainerXS: {
    marginLeft: 12,
  },
  detailsBtnContainerSM: {
    marginLeft: 'auto',
  },
  closedLabel: {
    color: theme.colors.CAUTION,
  },
  ctaContainer: {
    paddingTop: theme.spacing['1'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationResultsCardProps = {
  readonly location: Location;
  readonly withBottomBorder?: boolean;
  readonly withDetailsModal?: boolean;
  readonly locationSearchType: 'pickup' | 'delivery' | 'outpost';
  readonly isDisabled?: boolean;
  readonly isLoading?: boolean;
  readonly onLayout?: (
    listItemRef: RefObject<View>,
    listItemId: string,
  ) => void;
  readonly onPress: OnSelectedLocationCardPress;
  readonly onFocus?: (id: string | undefined) => void;
  readonly onUmount?: (id: Location['id']) => void;
};
